import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@clerk/react";
import { makeApi } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Search } from "lucide-react";
import { toast } from "sonner";

export default function AdminSubjects() {
  const { getToken } = useAuth();
  const [subjects, setSubjects] = useState([]);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("UGC_NET");

  useEffect(() => {
    makeApi(getToken).get("/subjects").then((r) => setSubjects(r.data));
  }, [getToken]);

  const setStatus = async (s, verification_status) => {
    try {
      await makeApi(getToken).patch(`/admin/subjects/${s.family}/${s.code}`, { verification_status });
      setSubjects((cur) => cur.map((x) => (x.family === s.family && x.code === s.code ? { ...x, verification_status } : x)));
      toast.success("Subject updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const filtered = useMemo(() => {
    const list = subjects.filter((s) => s.family === tab);
    if (!q.trim()) return list;
    const t = q.toLowerCase();
    return list.filter((s) => s.name.toLowerCase().includes(t) || s.code.includes(t));
  }, [subjects, tab, q]);

  const counts = useMemo(() => ({
    UGC_NET: subjects.filter((s) => s.family === "UGC_NET").length,
    CSIR_NET: subjects.filter((s) => s.family === "CSIR_NET").length,
  }), [subjects]);

  return (
    <div className="space-y-6" data-testid="admin-subjects-page">
      <div>
        <h1 className="font-display font-bold text-2xl sm:text-3xl text-slate-900 tracking-tight">Exam families & subjects</h1>
        <p className="text-slate-600 mt-2 text-sm">87 UGC NET subjects and 5 CSIR NET subject families, seeded on startup.</p>
      </div>

      <Card className="p-5 border-slate-200">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-slate-100">
            <TabsTrigger value="UGC_NET" data-testid="admin-tab-ugc">UGC NET ({counts.UGC_NET})</TabsTrigger>
            <TabsTrigger value="CSIR_NET" data-testid="admin-tab-csir">CSIR NET ({counts.CSIR_NET})</TabsTrigger>
          </TabsList>
          {["UGC_NET", "CSIR_NET"].map((f) => (
            <TabsContent key={f} value={f} className="mt-5">
              <div className="relative mb-4">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input placeholder="Filter by code or name" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="admin-subjects-search" />
              </div>
              <div className="rounded-lg border border-slate-200 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-24">Code</TableHead>
                      <TableHead>Subject</TableHead>
                      <TableHead className="w-40">Verification</TableHead>
                      <TableHead className="w-48">Blueprint</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((s) => (
                      <TableRow key={`${s.family}-${s.code}`} data-testid={`admin-subject-row-${s.family}-${s.code}`}>
                        <TableCell className="font-mono text-xs text-slate-500">{s.code}</TableCell>
                        <TableCell className="text-slate-900">{s.name}</TableCell>
                        <TableCell>
                          <select
                            value={s.verification_status}
                            onChange={(e) => setStatus(s, e.target.value)}
                            data-testid={`admin-subject-verify-${s.family}-${s.code}`}
                            className={`h-8 rounded-md border px-2 text-xs ${s.verification_status === "verified" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}
                          >
                            {["pending", "verified", "conflict"].map((v) => <option key={v} value={v}>{v}</option>)}
                          </select>
                        </TableCell>
                        <TableCell className="text-xs font-mono text-slate-500">{s.blueprint_version || "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </Card>
    </div>
  );
}
